import React, { useState, useEffect } from 'react';
import { EditPageTextField, EditPageSelect, EditPageButton, EditPageFooter } from '../../common/EditPage';
import { update } from '../../common/v4/BackendProvider';
import { toast } from 'react-toastify';

const ProfileForm = ({ profile, onUpdated }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        timezone: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [timezones, setTimezones] = useState({});

    useEffect(() => {
        if (profile) {
            setFormData({
                name: profile.name || '',
                email: profile.email || '',
                timezone: profile.timezone || 'UTC',
                phone: profile.phone || ''
            });
        }
        loadTimezones();
    }, [profile]);

    const loadTimezones = () => {
        // Common timezones - you can expand this list
        const commonTimezones = {
            'UTC': 'UTC',
            'America/New_York': 'America/New_York', 
            'America/Chicago': 'America/Chicago',
            'America/Denver': 'America/Denver',
            'America/Los_Angeles': 'America/Los_Angeles',
            'America/Vancouver': 'America/Vancouver',
            'America/Edmonton': 'America/Edmonton', 
            'America/Winnipeg': 'America/Winnipeg',
            'America/Toronto': 'America/Toronto',
            'America/Montreal': 'America/Montreal',
            'America/Halifax': 'America/Halifax',
            'America/St_Johns': 'America/St_Johns',
            'Europe/London': 'Europe/London',
            'Europe/Paris': 'Europe/Paris',
            'Europe/Berlin': 'Europe/Berlin',
            'Europe/Madrid': 'Europe/Madrid',
            'Europe/Rome': 'Europe/Rome',
            'Europe/Amsterdam': 'Europe/Amsterdam',
            'Europe/Brussels': 'Europe/Brussels',
            'Europe/Vienna': 'Europe/Vienna',
            'Europe/Stockholm': 'Europe/Stockholm',
            'Europe/Copenhagen': 'Europe/Copenhagen',
            'Europe/Oslo': 'Europe/Oslo',
            'Europe/Helsinki': 'Europe/Helsinki',
            'Europe/Riga': 'Europe/Riga',
            'Asia/Tokyo': 'Asia/Tokyo',
            'Asia/Shanghai': 'Asia/Shanghai',
            'Australia/Sydney': 'Australia/Sydney',
            'Pacific/Auckland': 'Pacific/Auckland'
        };
        setTimezones(commonTimezones);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            toast.error('Name is required');
            return;
        }

        if (!formData.email.trim()) {
            toast.error('Email is required');
            return;
        }

        try {
            setLoading(true);
            
            const updateData = {};
            if (formData.name !== profile.name) updateData.name = formData.name;
            if (formData.email !== profile.email) updateData.email = formData.email;
            if (formData.timezone !== profile.timezone) updateData.timezone = formData.timezone;
            if (formData.phone !== profile.phone) updateData.phone = formData.phone;

            if (Object.keys(updateData).length === 0) {
                toast.info('No changes to save');
                return;
            }

            await update('/client/profile', null, { data: updateData });
            
            // Don't show success toast here - parent component handles it
            if (onUpdated) {
                onUpdated();
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            const errorMessage = error.response?.data?.message || 'Error updating profile';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        if (profile) {
            setFormData({
                name: profile.name || '',
                email: profile.email || '',
                timezone: profile.timezone || 'UTC',
                phone: profile.phone || ''
            });
        }
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <div className="panel panel-default">
                    <div className="panel-body" style={{ paddingBottom: '0' }}>
                        <div className="row">
                            <div className="col-md-6">
                                <EditPageTextField
                                    id="name"
                                    label="Full Name *"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter your full name"
                                />
                            </div>
                            <div className="col-md-6">
                                <EditPageTextField
                                    id="email"
                                    label="Email Address *"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter your email address"
                                    type="email"
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <EditPageTextField
                                    id="phone"
                                    label="Phone Number"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter your phone number"
                                    type="tel"
                                />
                            </div>
                            <div className="col-md-6">
                                <EditPageSelect
                                    id="timezone"
                                    label="Timezone"
                                    value={formData.timezone}
                                    onChange={handleInputChange}
                                    options={timezones}
                                    nullable={false}
                                />
                            </div>
                        </div>
                    </div>

                    <EditPageFooter>
                        <EditPageButton
                            value="Reset"
                            handleClick={handleReset}
                            disabled={loading}
                            bsStyle="default"
                        />
                        <EditPageButton
                            value={loading ? 'Updating...' : 'Update Profile'}
                            handleClick={handleSubmit}
                            disabled={loading}
                            bsStyle="primary"
                        />
                    </EditPageFooter>
                </div>
            </div>
        </div>
    );
};

export default ProfileForm; 